import { useState } from "react";
import { useInView } from "react-intersection-observer";
import CountUp from "react-countup";
import { Link } from "react-router-dom";
import {
  TrendingUp, Shield, Landmark, Target,
  ArrowRight, CheckCircle, MessageCircle,
  Users, Award, Clock, Handshake,
  ChevronRight
} from "lucide-react";
import LeadCaptureModal from "@/components/LeadCaptureModal";

const ADVISOR_IMAGE = "https://customer-assets.emergentagent.com/job_sagar-fintech/artifacts/ihbvsnqh_Photo%20New.jpeg";
const HERO_IMAGE = "https://images.unsplash.com/photo-1774494168068-0f716c3aafcf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjbGVhbiUyMGZpbmFuY2UlMjBvZmZpY2V8ZW58MHx8fHwxNzc0ODA3NTQ3fDA&ixlib=rb-4.1.0&q=85";

function ScrollReveal({ children, className, delay = 0 }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div
      ref={ref}
      className={className || ""}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.7s ease-out ${delay}ms, transform 0.7s ease-out ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function AnimatedCounter({ end, suffix = "", label, icon: Icon }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
  return (
    <div ref={ref} className="text-center" data-testid={`counter-${label.replace(/\s/g, '-').toLowerCase()}`}>
      <div className="w-14 h-14 rounded-2xl bg-[#3B82F6]/10 flex items-center justify-center mx-auto mb-3">
        <Icon className="w-6 h-6 text-[#3B82F6]" />
      </div>
      <div className="font-['Outfit'] text-3xl md:text-4xl font-bold text-[#0B1F3A]">
        {inView ? <CountUp end={end} duration={2.5} suffix={suffix} /> : `0${suffix}`}
      </div>
      <p className="text-sm text-gray-500 mt-1">{label}</p>
    </div>
  );
}

export default function HomePage() {
  const [showLeadModal, setShowLeadModal] = useState(false);

  return (
    <div data-testid="home-page">
      {/* ====== HERO SECTION ====== */}
      <section className="relative min-h-[90vh] flex items-center pt-20 pb-12 md:pt-28 md:pb-20 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F9FAFB] via-white to-[#EFF6FF] -z-10" />
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-[#3B82F6]/5 rounded-full blur-3xl -z-10" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left */}
            <div>
              <ScrollReveal>
                <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#3B82F6] mb-4">
                  Trusted Financial Advisory
                </span>
              </ScrollReveal>

              <ScrollReveal delay={100}>
                <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight text-[#0B1F3A] mb-5">
                  Secure Your Financial{" "}
                  <span className="text-gradient">Future</span> with Confidence
                </h1>
              </ScrollReveal>

              <ScrollReveal delay={200}>
                <p className="text-base md:text-lg leading-relaxed text-gray-600 mb-8 max-w-lg">
                  20+ Years of Expertise in Investments & Insurance Solutions.
                  Start smart, invest right, and build lasting wealth.
                </p>
              </ScrollReveal>

              <ScrollReveal delay={300}>
                <div className="flex flex-wrap gap-3 mb-8">
                  <button
                    onClick={() => setShowLeadModal(true)}
                    className="bg-[#0B1F3A] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#111827] transition-all shadow-lg hover:shadow-xl btn-glow flex items-center gap-2"
                    data-testid="cta-hero-button"
                  >
                    Get Free Financial Plan
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <a
                    href="whatsapp://send?phone=919324555493&text=Hi%20Sagar,%20I%20need%20help%20with%20financial%20planning."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#25D366] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#20BD5A] transition-all shadow-md flex items-center gap-2"
                    data-testid="cta-whatsapp-button"
                  >
                    <MessageCircle className="w-4 h-4" fill="white" />
                    Chat on WhatsApp
                  </a>
                </div>
              </ScrollReveal>

              <ScrollReveal delay={400}>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4 text-[#3B82F6]" />
                    75+ Families Served
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4 text-[#3B82F6]" />
                    AMFI Certified
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4 text-[#3B82F6]" />
                    NJ Group & RenewBuy Partner
                  </span>
                </div>
              </ScrollReveal>
            </div>

            {/* Right */}
            <ScrollReveal delay={200} className="relative hidden lg:block">
              <div className="relative">
                <img
                  src={HERO_IMAGE}
                  alt="Modern financial office"
                  className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
                  data-testid="hero-image"
                />
                {/* Floating card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Portfolio Growth</p>
                      <p className="font-['Outfit'] font-semibold text-[#0B1F3A]">+12.4% CAGR</p>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4 border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#3B82F6]" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Happy Clients</p>
                      <p className="font-['Outfit'] font-semibold text-[#0B1F3A]">75+ Families</p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ====== GOALS SECTION ====== */}
      <section className="py-20 md:py-28 bg-white" data-testid="goals-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#3B82F6] mb-3 block">
              Your Financial Journey
            </span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0B1F3A]">
              What Do You Want to Achieve?
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: TrendingUp,
                title: "Grow Wealth",
                desc: "Build long-term wealth through Mutual Funds & SIP strategies tailored to your risk profile.",
                color: "#3B82F6",
                link: "/services#mutual-funds"
              },
              {
                icon: Shield,
                title: "Protect Family",
                desc: "Comprehensive insurance solutions for health, life, and motor through our RenewBuy & NJ Group partnerships.",
                color: "#10B981",
                link: "/services#insurance"
              },
              {
                icon: Landmark,
                title: "Save Tax",
                desc: "Smart tax optimization through ELSS, NPS, and strategic financial planning.",
                color: "#D4AF37",
                link: "/services#tax-planning"
              },
            ].map((goal, i) => (
              <ScrollReveal key={goal.title} delay={i * 150}>
                <Link
                  to={goal.link}
                  className="block bg-white border border-gray-100 rounded-2xl p-8 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] transition-all duration-300 hover:shadow-[0_12px_32px_-8px_rgba(0,0,0,0.12)] hover:-translate-y-1.5 group"
                  data-testid={`goal-card-${goal.title.replace(/\s/g, '-').toLowerCase()}`}
                >
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                    style={{ backgroundColor: `${goal.color}10` }}
                  >
                    <goal.icon className="w-6 h-6" style={{ color: goal.color }} />
                  </div>
                  <h3 className="font-['Outfit'] text-xl font-semibold text-[#0B1F3A] mb-2">
                    {goal.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">
                    {goal.desc}
                  </p>
                  <span className="text-sm font-medium text-[#3B82F6] flex items-center gap-1 group-hover:gap-2 transition-all">
                    Learn more <ChevronRight className="w-4 h-4" />
                  </span>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ====== ABOUT SECTION ====== */}
      <section className="py-20 md:py-28 bg-[#F9FAFB]" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <ScrollReveal>
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full rounded-2xl bg-[#3B82F6]/10 -z-10" />
                <img
                  src={ADVISOR_IMAGE}
                  alt="Sagar R Salunkhe - Financial Advisor"
                  className="rounded-2xl shadow-xl w-full max-w-md mx-auto lg:mx-0 object-cover aspect-[3/4]"
                  data-testid="advisor-profile-image"
                />
                <div className="absolute -bottom-4 -right-4 bg-[#D4AF37] text-[#0B1F3A] rounded-2xl px-5 py-3 shadow-lg">
                  <p className="font-['Outfit'] font-bold text-lg">20+</p>
                  <p className="text-xs font-medium">Years Experience</p>
                </div>
              </div>
            </ScrollReveal>

            {/* Content */}
            <div>
              <ScrollReveal>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#3B82F6] mb-3 block">
                  About Your Advisor
                </span>
              </ScrollReveal>
              <ScrollReveal delay={100}>
                <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0B1F3A] mb-5">
                  Meet Sagar R Salunkhe
                </h2>
              </ScrollReveal>
              <ScrollReveal delay={200}>
                <p className="text-base md:text-lg leading-relaxed text-gray-600 mb-6">
                  With over 20 years of experience in the investment & securities market,
                  I help individuals and families make smarter financial decisions with confidence.
                  My approach is simple: understand your goals, create a personalized plan, and
                  guide you every step of the way.
                </p>
              </ScrollReveal>
              <ScrollReveal delay={300}>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {[
                    { label: "AMFI Certified", value: "ARN-163413" },
                    { label: "Clients Served", value: "75+ Families" },
                    { label: "Experience", value: "20+ Years" },
                    { label: "MF Partner", value: "NJ Group" },
                    { label: "Insurance Partner", value: "RenewBuy" },
                  ].map((item) => (
                    <div key={item.label} className="bg-white rounded-xl p-4 border border-gray-100">
                      <p className="text-xs text-gray-500 mb-0.5">{item.label}</p>
                      <p className="font-['Outfit'] font-semibold text-[#0B1F3A] text-sm">{item.value}</p>
                    </div>
                  ))}
                </div>
              </ScrollReveal>
              <ScrollReveal delay={400}>
                <button
                  onClick={() => setShowLeadModal(true)}
                  className="bg-[#3B82F6] text-white font-semibold px-7 py-3 rounded-full hover:bg-[#2563EB] transition-all shadow-md flex items-center gap-2"
                  data-testid="about-cta-button"
                >
                  Schedule a Free Consultation
                  <ArrowRight className="w-4 h-4" />
                </button>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* ====== SOLUTIONS SECTION ====== */}
      <section className="py-20 md:py-28 bg-white" data-testid="solutions-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#3B82F6] mb-3 block">
              Our Solutions
            </span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0B1F3A]">
              Comprehensive Financial Solutions
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: TrendingUp,
                title: "Wealth Creation Plan",
                features: ["SIP & Lump Sum Investments", "Diversified Portfolio", "Regular Rebalancing", "Goal-based Planning"],
                accent: "#3B82F6"
              },
              {
                icon: Shield,
                title: "Protection Plan",
                features: ["Health Insurance", "Life Insurance", "Motor Insurance", "Critical Illness Cover"],
                accent: "#10B981"
              },
              {
                icon: Landmark,
                title: "Tax Saving Plan",
                features: ["ELSS Mutual Funds", "NPS Investments", "Tax Regime Analysis", "80C Optimization"],
                accent: "#D4AF37"
              },
            ].map((sol, i) => (
              <ScrollReveal key={sol.title} delay={i * 150}>
                <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] transition-all duration-300 hover:shadow-[0_12px_32px_-8px_rgba(0,0,0,0.12)] hover:-translate-y-1 h-full" data-testid={`solution-card-${i}`}>
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                    style={{ backgroundColor: `${sol.accent}10` }}
                  >
                    <sol.icon className="w-6 h-6" style={{ color: sol.accent }} />
                  </div>
                  <h3 className="font-['Outfit'] text-xl font-semibold text-[#0B1F3A] mb-4">
                    {sol.title}
                  </h3>
                  <ul className="space-y-2.5">
                    {sol.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: sol.accent }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ====== HOW IT WORKS ====== */}
      <section className="py-20 md:py-28 bg-[#F9FAFB]" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#3B82F6] mb-3 block">
              Simple Process
            </span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0B1F3A]">
              How It Works
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connector line (desktop) */}
            <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-[#3B82F6] via-[#D4AF37] to-[#10B981]" />

            {[
              { step: "01", icon: Target, title: "Share Your Goals", desc: "Tell us about your financial goals, risk appetite, and investment timeline." },
              { step: "02", icon: Award, title: "Get Personalized Plan", desc: "We create a customized financial plan tailored to your unique needs." },
              { step: "03", icon: TrendingUp, title: "Start & Track Easily", desc: "Begin your investment journey and track progress with regular reviews." },
            ].map((item, i) => (
              <ScrollReveal key={item.step} delay={i * 200}>
                <div className="text-center relative z-10" data-testid={`how-step-${i + 1}`}>
                  <div className="w-16 h-16 rounded-full bg-white border-2 border-[#3B82F6] flex items-center justify-center mx-auto mb-5 shadow-lg">
                    <item.icon className="w-6 h-6 text-[#3B82F6]" />
                  </div>
                  <span className="text-xs font-bold text-[#D4AF37] tracking-wider">STEP {item.step}</span>
                  <h3 className="font-['Outfit'] text-lg font-semibold text-[#0B1F3A] mt-2 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 max-w-xs mx-auto">
                    {item.desc}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ====== TRUST METRICS ====== */}
      <section className="py-20 md:py-28 bg-white" data-testid="trust-metrics-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal className="text-center mb-14">
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0B1F3A]">
              Numbers That Speak Trust
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <AnimatedCounter end={20} suffix="+" label="Years Experience" icon={Clock} />
            <AnimatedCounter end={75} suffix="+" label="Clients Served" icon={Users} />
            <AnimatedCounter end={100} suffix="%" label="Dedicated Assistance" icon={Handshake} />
            <AnimatedCounter end={2} suffix="" label="Trusted Partners" icon={Shield} />
          </div>
        </div>
      </section>

      {/* ====== FINAL CTA ====== */}
      <section
        className="py-20 md:py-28 relative overflow-hidden"
        data-testid="final-cta-section"
        style={{ background: "linear-gradient(135deg, #0B1F3A 0%, #132D5E 100%)" }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#3B82F6] blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-[#D4AF37] blur-3xl" />
        </div>

        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center relative z-10">
          <ScrollReveal>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-4">
              Get Your Free Financial Plan Today
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <p className="text-gray-300 text-base md:text-lg mb-8">
              Quick response within 15 minutes. No obligations, just expert advice.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="whatsapp://send?phone=919324555493&text=Hi%20Sagar,%20I%20need%20a%20free%20financial%20plan."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] text-white font-semibold px-8 py-3.5 rounded-full hover:bg-[#20BD5A] transition-all shadow-lg flex items-center gap-2"
                data-testid="final-cta-whatsapp"
              >
                <MessageCircle className="w-5 h-5" fill="white" />
                WhatsApp Now
              </a>
              <a
                href="tel:+917506020708"
                className="bg-white text-[#0B1F3A] font-semibold px-8 py-3.5 rounded-full hover:bg-gray-100 transition-all shadow-lg flex items-center gap-2"
                data-testid="final-cta-call"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                Call Now
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <LeadCaptureModal open={showLeadModal} onOpenChange={setShowLeadModal} />
    </div>
  );
}
